import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Loader2, Grid3X3, CheckCircle, AlertTriangle, XCircle, Clock
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Status colors matching the main matrix
const STATUS_COLORS = {
  valid: 'bg-green-500',
  expiring: 'bg-yellow-500',
  expired: 'bg-red-500',
  missing: 'bg-gray-300',
};

const STATUS_LABELS = {
  valid: 'Valid',
  expiring: 'Expiring Soon',
  expired: 'Expired',
  missing: 'Not Completed',
};

// Calculate status based on expiry date
const getStatus = (expiryDate) => {
  if (!expiryDate) return 'missing';

  const now = new Date();
  const expiry = new Date(expiryDate);
  const daysUntilExpiry = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));

  if (daysUntilExpiry < 0) return 'expired';
  if (daysUntilExpiry <= 30) return 'expiring';
  return 'valid';
};

export function TrainingMatrixMiniTile({ filter, size, useMockData }) {
  const navigate = useNavigate();
  const { userProfile, company } = useAuth();
  const [matrixData, setMatrixData] = useState({ members: [], requirements: [], cells: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!company?.id) {
      setLoading(false);
      return;
    }

    const loadMatrixData = async () => {
      try {
        setLoading(true);

        // Step 1: Get assignments (defines who should have what requirements)
        // Note: team_member_id actually references user_profiles.id
        let assignmentsQuery = supabase
          .from('compliance_assignments')
          .select(`
            id,
            team_member_id,
            requirement_id,
            project_id,
            user:user_profiles!compliance_assignments_team_member_id_fkey(id, first_name, last_name, company_id),
            requirement:compliance_requirements!compliance_assignments_requirement_id_fkey(id, name, show_on_matrix)
          `);

        // Filter by project if space is selected
        if (filter?.spaceId) {
          assignmentsQuery = assignmentsQuery.eq('project_id', filter.spaceId);
        }

        const { data: assignmentsData, error: assignmentsError } = await assignmentsQuery;
        if (assignmentsError) throw assignmentsError;

        // Filter to company members and requirements shown on matrix
        const filteredAssignments = (assignmentsData || []).filter(a =>
          a.user?.company_id === company.id &&
          a.requirement?.show_on_matrix !== false
        );

        if (filteredAssignments.length === 0) {
          setMatrixData({ members: [], requirements: [], cells: {} });
          setLoading(false);
          return;
        }

        // Extract unique members
        const membersMap = new Map();
        filteredAssignments.forEach(a => {
          if (a.user && !membersMap.has(a.team_member_id)) {
            membersMap.set(a.team_member_id, a.user);
          }
        });
        const members = Array.from(membersMap.values()).slice(0, 8);
        const memberIds = members.map(m => m.id);

        // Extract unique requirements (by name to handle duplicates)
        const requirementsMap = new Map();
        filteredAssignments.forEach(a => {
          if (a.requirement && !requirementsMap.has(a.requirement.name)) {
            requirementsMap.set(a.requirement.name, a.requirement);
          }
        });
        const requirements = Array.from(requirementsMap.values()).slice(0, 6);
        const requirementNames = requirements.map(r => r.name);

        // Step 2: Get user_qualifications (has actual expiry dates)
        const { data: qualificationsData, error: qualificationsError } = await supabase
          .from('user_qualifications')
          .select(`
            user_id,
            expiry_date,
            status,
            requirement:compliance_requirements!user_qualifications_requirement_id_fkey(name)
          `)
          .in('user_id', memberIds);

        if (qualificationsError) throw qualificationsError;

        // Build a map of user_id + requirement_name -> expiry_date
        const qualsMap = new Map();
        (qualificationsData || []).forEach(q => {
          if (q.requirement?.name) {
            const key = `${q.user_id}_${q.requirement.name}`;
            // Keep the best qualification (most recent expiry)
            const existing = qualsMap.get(key);
            if (!existing || (q.expiry_date && (!existing.expiry_date || q.expiry_date > existing.expiry_date))) {
              qualsMap.set(key, q);
            }
          }
        });

        // Step 3: Build cells map by matching user + requirement NAME
        const cells = {};

        // First, mark all cells as missing
        memberIds.forEach(memberId => {
          requirementNames.forEach(reqName => {
            cells[`${memberId}-${reqName}`] = 'missing';
          });
        });

        // Then update with qualification status (matched by NAME)
        memberIds.forEach(memberId => {
          requirementNames.forEach(reqName => {
            const key = `${memberId}_${reqName}`;
            const qual = qualsMap.get(key);
            if (qual) {
              cells[`${memberId}-${reqName}`] = getStatus(qual.expiry_date);
            }
          });
        });

        setMatrixData({
          members,
          requirements,
          cells
        });
      } catch (error) {
        console.error('Failed to load matrix data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadMatrixData();
  }, [company?.id, filter?.spaceId]);

  const goToMatrix = () => {
    if (filter?.spaceId) {
      navigate(`/space/${filter.spaceId}/matrix`);
    } else {
      navigate('/qualifications');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-violet-600" />
      </div>
    );
  }

  const { members, requirements, cells } = matrixData;

  if (members.length === 0 || requirements.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-4">
        <div className="bg-gray-100 p-3 rounded-full mb-3">
          <Grid3X3 className="h-6 w-6 text-gray-400" />
        </div>
        <p className="text-sm font-medium text-gray-900">No matrix data</p>
        <p className="text-xs text-gray-500 mt-1">Assign requirements to team members</p>
      </div>
    );
  }

  // Calculate summary stats
  const allStatuses = Object.values(cells);
  const stats = {
    valid: allStatuses.filter(s => s === 'valid').length,
    expiring: allStatuses.filter(s => s === 'expiring').length,
    expired: allStatuses.filter(s => s === 'expired').length,
    missing: allStatuses.filter(s => s === 'missing').length,
  };

  return (
    <TooltipProvider delayDuration={100}>
      <div className="h-full flex flex-col overflow-hidden">
        {/* Summary stats */}
        <div className="flex items-center gap-2 mb-2 pb-2 border-b shrink-0">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            <span className="text-[10px] text-gray-600">{stats.valid}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-yellow-500" />
            <span className="text-[10px] text-gray-600">{stats.expiring}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-red-500" />
            <span className="text-[10px] text-gray-600">{stats.expired}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-gray-300" />
            <span className="text-[10px] text-gray-600">{stats.missing}</span>
          </div>
        </div>

        {/* Mini Matrix Grid */}
        <ScrollArea className="flex-1 min-h-0">
          <div className="pr-2">
            {/* Header row with requirement names */}
            <div className="flex items-center gap-1 mb-1 sticky top-0 bg-white pb-1">
              <div className="w-20 shrink-0" /> {/* Spacer for name column */}
              {requirements.map((req) => (
                <Tooltip key={req.id}>
                  <TooltipTrigger asChild>
                    <div className="w-6 h-6 flex items-center justify-center">
                      <span className="text-[8px] font-medium text-gray-500 truncate transform -rotate-45 origin-center whitespace-nowrap">
                        {req.name.substring(0, 3)}
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p className="text-xs">{req.name}</p>
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>

            {/* Member rows */}
            {members.map((member) => (
              <div key={member.id} className="flex items-center gap-1 mb-1">
                {/* Member name */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="w-20 shrink-0 truncate">
                      <span className="text-[10px] font-medium text-gray-700">
                        {member.first_name} {member.last_name?.[0]}.
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="left">
                    <p className="text-xs">{member.first_name} {member.last_name}</p>
                  </TooltipContent>
                </Tooltip>

                {/* Status cells */}
                {requirements.map((req) => {
                  const status = cells[`${member.id}-${req.name}`] || 'missing';
                  return (
                    <Tooltip key={`${member.id}-${req.name}`}>
                      <TooltipTrigger asChild>
                        <div
                          className={cn(
                            "w-6 h-6 rounded-sm cursor-pointer transition-all hover:scale-110",
                            STATUS_COLORS[status]
                          )}
                          onClick={goToMatrix}
                        />
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <p className="text-xs font-medium">{member.first_name} {member.last_name}</p>
                        <p className="text-xs text-gray-500">{req.name}</p>
                        <p className="text-xs">{STATUS_LABELS[status]}</p>
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            ))}
          </div>
        </ScrollArea>

        {/* Footer */}
        <button
          onClick={goToMatrix}
          className="mt-2 pt-2 border-t flex items-center justify-center gap-1 text-xs text-violet-600 hover:text-violet-800 font-medium transition-colors shrink-0"
        >
          <Grid3X3 className="h-3.5 w-3.5" />
          <span>View Full Matrix</span>
        </button>
      </div>
    </TooltipProvider>
  );
}
